// ReusableForm.jsx (Fixed checkbox styling and added suffix support for text/password inputs)
import React, { useState, useEffect, useRef } from 'react';
import { InputText }      from 'primereact/inputtext';
import { InputTextarea }  from 'primereact/inputtextarea';
import { Dropdown }       from 'primereact/dropdown';
import { MultiSelect }    from 'primereact/multiselect';
import { Button }         from 'primereact/button';
import { Calendar }       from 'primereact/calendar';
import { Checkbox }       from 'primereact/checkbox';

/**
 * A generically-typed form that renders fields based on a config array.
 */
const ReusableForm = ({
  formData,
  setFormData,
  fields,
  onSubmit,
  onHide,
  onChange,           // global change handler (optional)
  loading       = false,
  submitLabel   = 'Save',
  buttonClassName = '',
  hideCancelButton = false,
  headerText    = ''
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const formRef = useRef(null);

  /** ------------------------------------------------------------------
   * Unified change handler – works for PrimeReact (e.value),
   * native inputs (e.target.value) and file inputs (e.target.files)
   * ------------------------------------------------------------------ */
  const handleChange = (e, field) => {
    const name  = field.name;
    let   value = e?.value ?? e?.target?.value;

    // Special case: <input type="file" />
    if (field.type === 'file') {
      value = e.target.files?.[0] ?? null;
    }
    // Special case: Calendar component returns a Date object directly in e.value
    // Checkbox component returns boolean in e.target.checked if it's a native input
    // If using PrimeReact Checkbox, it returns boolean in e.checked
    else if (field.type === 'checkbox') {
        // For PrimeReact Checkbox: value = e.checked;
        value = e.checked || e.target?.checked;
    }

    setFormData(prev => ({ ...prev, [name]: value }));

    /* field-level change hook --------------------------------------- */
    if (field.onChange) {
      field.onChange(e, field, formData, setFormData);
    }

    /* form-level change hook ---------------------------------------- */
    if (onChange) {
      onChange(e, field);
    }
  };

  /* --------------------------------------------------------------- */
  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(e);            // delegate to parent
  };

  /* -------- Full-screen helpers ---------------------------------- */
  const toggleFullscreen = () => {
    const elem = formRef.current?.closest('.p-dialog') || formRef.current;
    if (!elem) return;

    if (!document.fullscreenElement) {
      elem.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };

  useEffect(() => {
    const sync = () =>
      setIsFullscreen(
        !!(document.fullscreenElement ||
           document.mozFullScreenElement ||
           document.webkitFullscreenElement ||
           document.msFullscreenElement)
      );

    document.addEventListener('fullscreenchange',     sync);
    document.addEventListener('mozfullscreenchange',  sync);
    document.addEventListener('webkitfullscreenchange',sync);
    document.addEventListener('msfullscreenchange',   sync);

    return () => {
      document.removeEventListener('fullscreenchange',     sync);
      document.removeEventListener('mozfullscreenchange',  sync);
      document.removeEventListener('webkitfullscreenchange',sync);
      document.removeEventListener('msfullscreenchange',   sync);
    };
  }, []);

  /* --------------------------------------------------------------- */
  const renderField = (field) => {
    const {
      name, label, type, required, placeholder,
      options, helpText, disabled, onChange: fieldOnChange,
      loading: fieldLoading,
      dateFormat,
      checked,
      itemTemplate,         // NEW: itemTemplate prop for Dropdown
      selectedItemTemplate, // NEW: selectedItemTemplate prop for Dropdown
      virtualScrollerOptions, // NEW: virtualScrollerOptions for Dropdown
      actualInputType, // NEW: for dynamically changing input type (e.g., password toggle)
      suffix // NEW: for icons/buttons inside inputs
    } = field;

    const value     = formData[name];
    const labelBase = 'block text-sm font-medium text-gray-700 mb-2 tracking-wide';
    const inputBase = 'w-full p-3.5 rounded-xl border border-gray-200 bg-white shadow-sm ' +
                      'focus:ring-2 focus:ring-[#1e3a8a] focus:border-[#1e3a8a] ' +
                      'transition-all text-base placeholder-gray-400 outline-none hover:border-gray-300';

    /* ---- CUSTOM RENDER ------------------------------------------ */
    if (type === 'custom' && field.component) {
      return (
        <div className="mt-2">
          <label className={labelBase}>
            {label}{required && <span className="text-red-500 ml-1">*</span>}
          </label>

          {field.component({
            value,
            onChange: (e) => handleChange(e, field)
          })}

          {helpText && <p className="mt-1 text-sm text-gray-500">{helpText}</p>}
        </div>
      );
    }

    /* ---- STANDARD TYPES ----------------------------------------- */
    switch (type) {
      case 'text':
      case 'number':
      case 'password': // Handle password type
        return (
          <div className="mt-2">
            <label className={labelBase}>
              {label}{required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <div className="relative"> {/* Added relative positioning for suffix */}
              <InputText
                id={name}
                name={name}
                type={actualInputType || type} // Use actualInputType if provided, otherwise default to field.type
                value={value || ''}
                onChange={(e) => handleChange(e, field)}
                className={`${inputBase} ${suffix ? 'pr-10' : ''}`} // Add padding-right if suffix exists
                placeholder={placeholder || `Enter ${label.toLowerCase()}`}
                required={required}
              />
              {suffix && ( // Render suffix if provided
                <span className="absolute top-1/2 right-3 -translate-y-1/2 cursor-pointer">
                  {suffix}
                </span>
              )}
            </div>
            {helpText && <small className="text-gray-500">{helpText}</small>}
          </div>
        );

      case 'textarea':
        return (
          <div className="mt-2">
            <label className={labelBase}>
              {label}{required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <InputTextarea
              id={name}
              name={name}
              rows={3}
              value={value || ''}
              onChange={(e) => handleChange(e, field)}
              className={inputBase}
              placeholder={placeholder || `Enter ${label.toLowerCase()}`}
              required={required}
            />
            {helpText && <small className="text-gray-500">{helpText}</small>}
          </div>
        );

      case 'date':
        return (
          <div className="mt-2">
            <label className={labelBase}>
              {label}{required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <InputText
              id={name}
              name={name}
              type="date"
              value={value || ''}
              onChange={(e) => handleChange(e, field)}
              className={inputBase}
              placeholder={placeholder || `Select ${label.toLowerCase()}`}
              required={required}
            />
            {helpText && <small className="text-gray-500">{helpText}</small>}
          </div>
        );

      case 'calendar':
        return (
          <div className="mt-2">
            <label className={labelBase}>
              {label}{required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <Calendar
              id={name}
              name={name}
              value={value instanceof Date ? value : (value ? new Date(value) : null)}
              onChange={(e) => handleChange(e, field)}
              dateFormat={dateFormat || 'dd/mm/yy'}
              showIcon
              readOnlyInput={disabled}
              disabled={disabled}
              placeholder={placeholder || `Select ${label.toLowerCase()}`}
              className={`${inputBase} !py-0`}
              required={required}
              appendTo={formRef.current}
            />
            {helpText && <small className="text-gray-500">{helpText}</small>}
          </div>
        );

      case 'multiselect':
        return (
          <div className="mt-2">
            <label className={labelBase}>
              {label}{required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <MultiSelect
              id={name}
              name={name}
              value={value || []}
              options={options || []}
              optionLabel="label"
              optionValue="value"
              display="chip"
              filter
              placeholder={placeholder || `Select ${label.toLowerCase()}`}
              className="w-full rounded-xl border border-gray-200 shadow-sm focus:ring-2 focus:ring-[#1e3a8a]"
              required={required}
              disabled={disabled}
              loading={fieldLoading}
              onChange={(e) => handleChange(e, field)}
              appendTo={formRef.current}
            />
            {helpText && <small className="text-gray-500">{helpText}</small>}
          </div>
        );

      case 'select':
        return (
          <div className="mt-2">
            <label className={labelBase}>
              {label}{required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <Dropdown
              id={name}
              name={name}
              value={value ?? ''}
              options={options ?? []}
              optionLabel="label"
              optionValue="value"
              placeholder={placeholder || `Select ${label.toLowerCase()}`}
              className="w-full rounded-xl border border-gray-200 shadow-sm focus:ring-2 focus:ring-[#1e3a8a]"
              required={required}
              disabled={typeof disabled === 'function'
                          ? disabled(formData)
                          : disabled || fieldLoading}
              loading={fieldLoading}
              onChange={(e) => handleChange(e, field)}
              filter={field.filter}
              appendTo={document.body}
              itemTemplate={itemTemplate}          // NEW: Pass itemTemplate
              selectedItemTemplate={selectedItemTemplate} // NEW: Pass selectedItemTemplate
              virtualScrollerOptions={virtualScrollerOptions} // NEW: Pass virtualScrollerOptions
            />
            {helpText && <small className="text-gray-500">{helpText}</small>}
          </div>
        );

      case 'file':
        return (
          <div className="mt-2">
            <label className={labelBase}>
              {label}{required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              id={name}
              name={name}
              type="file"
              onChange={(e) => handleChange(e, field)}
              className={inputBase}
              required={required}
            />
            {helpText && <small className="text-gray-500">{helpText}</small>}
          </div>
        );

      case 'checkbox':
        return (
          <div className={`mt-2 mb-2 flex items-center ${field.className || ''}`}>
            <div className="checkbox-wrapper">
              <Checkbox
                inputId={name}
                checked={value ?? false}
                onChange={(e) => handleChange(e, field)}
                disabled={disabled}
                required={required}
              />
            </div>
            <label htmlFor={name} className="ml-2 text-sm font-medium text-gray-700 cursor-pointer">
              {label}{required && <span className="text-red-500 ml-1">*</span>}
            </label>
            {helpText && <small className="ml-2 text-gray-500">{helpText}</small>}
          </div>
        );

      default:
        return null;
    }
  };

  /* ---- Layout helpers ------------------------------------------- */
  const visibleFields = fields.filter(f => f && f.visible !== false);
  const total        = visibleFields.length;

  let gridCols   = 'grid-cols-1';
  let maxWidth   = 'max-w-md';
  if (total === 2)        { gridCols = 'grid-cols-2'; maxWidth = 'max-w-xl';   }
  else if (total === 3)   { gridCols = 'grid-cols-3'; maxWidth = 'max-2xl'; }
  else if (total > 6)     { gridCols = 'grid-cols-3'; maxWidth = 'max-w-4xl'; }
  else if (total > 3)     { gridCols = 'grid-cols-2'; maxWidth = 'max-w-2xl'; }

  const getSpan = (f) =>
    (f.type === 'textarea' || f.name.toLowerCase().includes('description') || f.width === 'col-span-full' || f.type === 'checkbox')
      ? (f.width || 'col-span-full') : (f.width || '');

  /* ---------------------------------------------------------------- */
  return (
    <>
      {/* Custom CSS for square checkbox styling */}
      <style jsx>{`
        .checkbox-wrapper .p-checkbox {
          width: 18px;
          height: 18px;
        }
        
        .checkbox-wrapper .p-checkbox .p-checkbox-box {
          width: 18px !important;
          height: 18px !important;
          border: 2px solid #6b7280 !important;
          border-radius: 3px !important;
          background-color: #ffffff !important;
          transition: all 0.2s ease !important;
          box-shadow: none !important;
        }
        
        .checkbox-wrapper .p-checkbox .p-checkbox-box:hover {
          border-color: #1e3a8a !important;
          background-color: #f8fafc !important;
        }
        
        .checkbox-wrapper .p-checkbox .p-checkbox-box.p-focus {
          border-color: #1e3a8a !important;
          box-shadow: 0 0 0 2px rgba(30, 58, 138, 0.2) !important;
        }
        
        .checkbox-wrapper .p-checkbox .p-checkbox-box.p-highlight {
          border-color: #1e3a8a !important;
          background-color: #ffffff !important;
        }
        
        .checkbox-wrapper .p-checkbox .p-checkbox-box .p-checkbox-icon {
          color: #1e3a8a !important;
          font-size: 12px !important;
          width: 12px !important;
          height: 12px !important;
        }
        
        .checkbox-wrapper .p-checkbox .p-checkbox-box.p-highlight .p-checkbox-icon {
          color: #1e3a8a !important;
        }
        
        .checkbox-wrapper .p-checkbox .p-checkbox-box.p-highlight .p-checkbox-icon:before {
          content: "✓" !important;
          font-weight: bold !important;
          color: #1e3a8a !important;
        }
        
        .checkbox-wrapper .p-checkbox:not(.p-checkbox-disabled) .p-checkbox-box:hover {
          border-color: #1e3a8a !important;
        }
        
        .checkbox-wrapper .p-checkbox.p-checkbox-disabled .p-checkbox-box {
          background-color: #f3f4f6 !important;
          border-color: #d1d5db !important;
          opacity: 0.6 !important;
        }
      `}</style>

      <div className="flex justify-center items-center w-full h-full">
        <form ref={formRef}
              onSubmit={handleSubmit}
              className={`p-0 max-h-[90vh] overflow-y-auto flex flex-col w-full bg-white rounded-2xl border border-gray-200 shadow-lg`}>

          {/* Header bar ------------------------------------------------ */}
          {headerText && (
            <div className="flex items-center justify-between px-6 py-4 bg-[#1e3a8a] rounded-t-xl
                            border-b border-gray-200 sticky top-0 z-10">
              <h3 className="text-xl font-bold text-white tracking-wide">{headerText}</h3>

              <div className="flex items-center gap-2">
                <Button type="button"
                        icon={isFullscreen ? 'pi pi-window-minimize'
                                           : 'pi pi-window-maximize'}
                        onClick={toggleFullscreen}
                        className="p-button-text text-white hover:text-gray-200"
                        tooltip="Toggle Fullscreen"
                        tooltipOptions={{ position: 'bottom' }} />

                {onHide && (
                  <Button type="button"
                          icon="pi pi-times"
                          onClick={onHide}
                          className="p-button-text text-white hover:text-gray-200"
                          tooltip="Close"
                          tooltipOptions={{ position: 'bottom' }} />
                )}
              </div>
            </div>
          )}

          {/* Body ------------------------------------------------------ */}
          <div className={`p-8 flex-1 overflow-y-auto ${total === 1 &&
            'flex flex-col justify-center items-center min-h-[300px]'}`}>

            {total === 1 ? (
              <div className="w-full max-w-lg">{renderField(visibleFields[0])}</div>
            ) : (
              <div className={`grid ${gridCols} gap-4 w-full`}>
                {visibleFields.map((field, i) => (
                  <div key={field.name || i} className={getSpan(field)}>
                    {renderField(field)}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer ---------------------------------------------------- */}
          <div className="flex justify-end space-x-2 mt-6 px-4 py-4 border-t border-gray-200
                          bg-white sticky bottom-0 z-10">
            {!hideCancelButton && (
              <Button type="button"
                      label="Cancel"
                      onClick={onHide}
                      className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100" />
            )}

            {submitLabel && (
              <Button type="submit"
                      label={submitLabel}
                      loading={loading}
                      className={`px-4 py-2 bg-[#1e3a8a] text-white rounded-md hover:bg-[#1e3a8a]/90 ${buttonClassName}`} />
            )}
          </div>
        </form>
      </div>
    </>
  );
};

export default ReusableForm;